<template>
    <v-container fluid class="ui-showcase-page" px-2>
        <header class="ui-showcase-heading">
            <div>
                <div class="ui-showcase-eyebrow">PRIVATE DESIGN AREA</div>
                <h1>UI showcase</h1>
                <p>Palette, controls, and safe sample previews of the app dialogs.</p>
            </div>
            <v-btn text color="primary" :to="{ name: 'settings' }">Back to Settings</v-btn>
        </header>

        <section class="ui-showcase-section">
            <h2>Palette</h2>
            <div class="ui-palette-grid">
                <div v-for="swatch in paletteSwatches" :key="swatch.name" class="ui-palette-swatch">
                    <div class="ui-palette-color" :style="{ backgroundColor: swatch.value }"></div>
                    <div class="ui-palette-copy">
                        <strong>{{ swatch.name }}</strong>
                        <span>{{ swatch.value }}</span>
                    </div>
                </div>
            </div>
        </section>

        <section class="ui-showcase-section">
            <h2>Buttons</h2>
            <v-card class="ui-showcase-card">
                <v-card-text class="ui-control-grid">
                    <v-btn color="primary">Primary</v-btn>
                    <v-btn color="secondary">Marine</v-btn>
                    <v-btn color="success">Success</v-btn>
                    <v-btn color="warning">Warning</v-btn>
                    <v-btn color="error">Error</v-btn>
                    <v-btn color="info">Info</v-btn>
                    <v-btn outlined color="primary">Outlined</v-btn>
                    <v-btn text color="primary">Text action</v-btn>
                    <v-btn color="primary" disabled>Disabled</v-btn>
                    <v-btn color="primary" :loading="buttonLoading" @click="toggleButtonLoading">
                        {{ buttonLoading ? "Loading" : "Loading state" }}
                    </v-btn>
                </v-card-text>
            </v-card>
        </section>

        <section class="ui-showcase-section">
            <h2>Form controls</h2>
            <v-card class="ui-showcase-card">
                <v-card-text class="ui-form-grid">
                    <v-text-field v-model="sampleText" label="Text field" outlined dense hide-details />
                    <v-select v-model="sampleSelect"
                              :items="['Primary', 'Secondary', 'Success']"
                              label="Select"
                              outlined
                              dense
                              hide-details />
                    <v-checkbox v-model="sampleCheckbox" label="Checkbox" hide-details class="ma-0" />
                    <v-switch v-model="sampleSwitch" label="Switch" hide-details class="ma-0" />
                    <v-radio-group v-model="sampleRadio" row class="ma-0">
                        <v-radio label="Option A" value="a" />
                        <v-radio label="Option B" value="b" />
                    </v-radio-group>
                    <div class="ui-slider-control">
                        <span>Slider value: {{ sampleSlider }}</span>
                        <v-slider v-model="sampleSlider" min="0" max="100" step="5" hide-details />
                    </div>
                </v-card-text>
            </v-card>
        </section>

        <section class="ui-showcase-section">
            <h2>Status and surfaces</h2>
            <v-row dense>
                <v-col cols="12" md="6">
                    <v-card class="ui-showcase-card">
                        <v-card-title>Status chips</v-card-title>
                        <v-card-text class="ui-chip-row">
                            <v-chip color="primary" text-color="white">Primary</v-chip>
                            <v-chip color="secondary" text-color="white">Marine</v-chip>
                            <v-chip color="success" text-color="white">Responsive</v-chip>
                            <v-chip color="warning" text-color="white">Warning</v-chip>
                            <v-chip color="error" text-color="white">Alarm</v-chip>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" md="6">
                    <v-card class="ui-showcase-card">
                        <v-card-title>Progress</v-card-title>
                        <v-card-text>
                            <v-progress-linear :value="68" color="primary" rounded height="8" />
                            <div class="ui-progress-row">
                                <v-progress-circular :value="68" color="secondary" size="44" width="4">
                                    68
                                </v-progress-circular>
                                <v-progress-circular indeterminate color="primary" size="36" width="3" />
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" md="6">
                    <v-card class="ui-showcase-card">
                        <v-card-title>Sensor list</v-card-title>
                        <v-list dense>
                            <v-list-item v-for="item in sampleSensors" :key="item.name">
                                <v-list-item-icon>
                                    <v-icon :color="item.color">{{ item.icon }}</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                                </v-list-item-content>
                                <v-list-item-action>
                                    <v-chip x-small :color="item.color" text-color="white">{{ item.status }}</v-chip>
                                </v-list-item-action>
                            </v-list-item>
                        </v-list>
                    </v-card>
                </v-col>
                <v-col cols="12" md="6">
                    <v-card class="ui-showcase-card">
                        <v-card-title>Inline alert</v-card-title>
                        <v-card-text>
                            <v-alert dense text type="info">Sample informational message.</v-alert>
                            <v-alert dense text type="success">Sample successful action.</v-alert>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </section>

        <section class="ui-showcase-section">
            <h2>Dialog previews</h2>
            <p class="ui-showcase-note">Previews use sample data and cannot call services or change device settings.</p>
            <div class="ui-dialog-grid">
                <v-btn v-for="demo in dialogDemos"
                       :key="demo.name"
                       outlined
                       color="primary"
                       class="ui-dialog-launcher"
                       @click="openDialogPreview(demo)">
                    {{ demo.name }}
                </v-btn>
            </div>
        </section>

        <component v-if="selectedDialog"
                   :is="selectedDialog.name"
                   v-bind="selectedDialog.props"
                   @input="closeDialogPreview"
                   @OnOk="closeDialogPreview"
                   @OnCancel="closeDialogPreview"
                   @closed="closeDialogPreview" />
        <v-btn v-if="selectedDialog"
               class="ui-showcase-preview-exit"
               color="primary"
               fab
               small
               aria-label="Exit dialog preview"
               @click="closeDialogPreview">
            <v-icon>mdi-close</v-icon>
        </v-btn>
    </v-container>
</template>

<script lang="ts">
    import { defineComponent, onBeforeUnmount, ref } from "vue";
    import { appPalette } from "@/theme/palette";

    interface DialogPreview {
        name: string;
        props: Record<string, unknown>;
    }

    const dialogDemos: DialogPreview[] = [
        { name: "ActionDialog", props: { value: true, title: "Device actions", deviceName: "Shore Power", deviceId: "demo-device", responsive: true, choiceMode: true } },
        { name: "AlarmDialog", props: { value: true, title: "Alarm triggered", message: "Sample alarm notification." } },
        { name: "AlertDialog", props: { value: true, title: "Alert", message: "Sample informational alert." } },
        { name: "AppNotificationDialog", props: { value: true, tone: "success", title: "Success", message: "Sample app notification.", showClose: true } },
        { name: "ArmingWizardDialog", props: { value: true, demoPreview: true } },
        { name: "ConfirmDialog", props: { value: true, title: "Remove sensor", message: "Do you want to remove this sample sensor?", options: { width: 400 } } },
        { name: "ConsumptionDialog", props: { value: true, meterId: "demo-meter", deviceType: 2, subtitle: "Sample battery", demoPreview: true } },
        { name: "CurrentDialog", props: { value: true, id: "demo-current", deviceType: 17, subtitle: "Sample bilge pump", demoPreview: true } },
        { name: "EditCurrentDialog", props: { value: true, demoPreview: true } },
        { name: "EditGasSensorDialog", props: { value: true, demoPreview: true } },
        { name: "EditGasValveDialog", props: { value: true, demoPreview: true } },
        { name: "EditNicknameDialog", props: { value: true, demoPreview: true } },
        { name: "EnrollDialog", props: { demoPreview: true } },
        { name: "GeofenceConfirmDialog", props: { value: true, initialLat: 55.71614, initialLng: 9.51657, ageSeconds: 18, hasInvalidAfterValid: true, geofenceRadiusMeters: 35, demoPreview: true } },
        { name: "HomeLocationDialog", props: { value: true, demoPreview: true } },
        { name: "ImageUploadDialog", props: { value: true, title: "Upload image", emptyStateText: "Sample image preview", showFileInput: false, showUpload: false, showDelete: false, demoPreview: true } },
        { name: "InfoDialog", props: { value: true, title: "Device information", message: "Sample informational content." } },
        { name: "LoadingDialog", props: { value: true, message: "Loading sample data…" } },
        { name: "LocationDialog", props: { value: true, demoPreview: true } },
        { name: "SensorInfoDialog", props: { value: true, deviceId: "demo-device", deviceOptions: [{ id: "demo-device", label: "Sample door sensor" }], demoPreview: true } },
        { name: "ShareDialog", props: { value: true, model: { headline: "Captain's Log", text: "Sample journey summary.", imageUrl: "" } } },
        { name: "ShorePowerPairDialog", props: { value: true, shorePowerControl: { attachedDeviceId: "demo-control", attachedDeviceTypeId: 2, age: 1 }, shorePowerMeter: { attachedDeviceId: "demo-meter", attachedDeviceTypeId: 14, age: 1 } } },
        { name: "SuccessDialog", props: { value: true, title: "Success", message: "Sample successful action." } },
        { name: "TemperatureSourceDialog", props: { value: true, demoPreview: true } },
        { name: "TimerDialog", props: { value: true, deviceType: 2, demoPreview: true } },
        { name: "UnpairDialog", props: { demoPreview: true } },
        { name: "VoltageDialog", props: { value: true, id: "demo-battery", deviceType: 2, subtitle: "Sample battery", demoPreview: true } },
        { name: "WarningDialog", props: { value: true, title: "Warning", message: "Sample warning notification." } },
    ];

    export default defineComponent({
        name: "UiShowcase",
        components: {
            ActionDialog: () => import("./components/Dialogs/ActionDialog.vue"),
            AlarmDialog: () => import("./components/Dialogs/AlarmDialog.vue"),
            AlertDialog: () => import("./components/Dialogs/AlertDialog.vue"),
            AppNotificationDialog: () => import("./components/Dialogs/AppNotificationDialog.vue"),
            ArmingWizardDialog: () => import("./components/Dialogs/ArmingWizardDialog.vue"),
            ConfirmDialog: () => import("./components/Dialogs/ConfirmDialog.vue"),
            ConsumptionDialog: () => import("./components/Dialogs/ConsumptionDialog.vue"),
            CurrentDialog: () => import("./components/Dialogs/CurrentDialog.vue"),
            EditCurrentDialog: () => import("./components/Dialogs/EditCurrentDialog.vue"),
            EditGasSensorDialog: () => import("./components/Dialogs/EditGasSensorDialog.vue"),
            EditGasValveDialog: () => import("./components/Dialogs/EditGasValveDialog.vue"),
            EditNicknameDialog: () => import("./components/Dialogs/EditNicknameDialog.vue"),
            EnrollDialog: () => import("./components/Dialogs/EnrollDialog.vue"),
            GeofenceConfirmDialog: () => import("./components/Dialogs/GeofenceConfirmDialog.vue"),
            HomeLocationDialog: () => import("./components/Dialogs/HomeLocationDialog.vue"),
            ImageUploadDialog: () => import("./components/Dialogs/ImageUploadDialog.vue"),
            InfoDialog: () => import("./components/Dialogs/InfoDialog.vue"),
            LoadingDialog: () => import("./components/Dialogs/LoadingDialog.vue"),
            LocationDialog: () => import("./components/Dialogs/LocationDialog.vue"),
            SensorInfoDialog: () => import("./components/Dialogs/SensorInfoDialog.vue"),
            ShareDialog: () => import("./components/Dialogs/ShareDialog.vue"),
            ShorePowerPairDialog: () => import("./components/Dialogs/ShorePowerPairDialog.vue"),
            SuccessDialog: () => import("./components/Dialogs/SuccessDialog.vue"),
            TemperatureSourceDialog: () => import("./components/Dialogs/TemperatureSourceDialog.vue"),
            TimerDialog: () => import("./components/Dialogs/TimerDialog.vue"),
            UnpairDialog: () => import("./components/Dialogs/UnpairDialog.vue"),
            VoltageDialog: () => import("./components/Dialogs/VoltageDialog.vue"),
            WarningDialog: () => import("./components/Dialogs/WarningDialog.vue"),
        },
        setup() {
            const sampleText = ref("Marine One");
            const sampleSelect = ref("Primary");
            const sampleCheckbox = ref(true);
            const sampleSwitch = ref(true);
            const sampleRadio = ref("a");
            const sampleSlider = ref(65);
            const buttonLoading = ref(false);
            const selectedDialog = ref<DialogPreview | null>(null);

            const paletteSwatches = Object.entries(appPalette).map(([name, value]) => ({
                name: name.replace(/[A-Z]/g, (letter) => ` ${letter.toLowerCase()}`),
                value,
            }));

            const sampleSensors = [
                { name: "Main door", icon: "mdi-door", color: "success", status: "Closed" },
                { name: "Gas detector", icon: "mdi-gas-cylinder", color: "warning", status: "Ready" },
                { name: "Bilge pump", icon: "mdi-water-pump", color: "error", status: "Off" },
            ];

            const openDialogPreview = (demo: DialogPreview) => {
                selectedDialog.value = demo;
                document.body.classList.add("ui-showcase-preview");
            };

            const closeDialogPreview = () => {
                document.body.classList.remove("ui-showcase-preview");
                selectedDialog.value = null;
            };

            onBeforeUnmount(() => document.body.classList.remove("ui-showcase-preview"));

            const toggleButtonLoading = () => {
                buttonLoading.value = !buttonLoading.value;
                if (buttonLoading.value) {
                    setTimeout(() => { buttonLoading.value = false; }, 900);
                }
            };

            return {
                appPalette,
                paletteSwatches,
                sampleSensors,
                sampleText,
                sampleSelect,
                sampleCheckbox,
                sampleSwitch,
                sampleRadio,
                sampleSlider,
                buttonLoading,
                dialogDemos,
                selectedDialog,
                openDialogPreview,
                closeDialogPreview,
                toggleButtonLoading,
            };
        },
    });
</script>

<style scoped lang="scss">
    .ui-showcase-page {
        min-height: 100%;
        padding: 16px 12px calc(76px + env(safe-area-inset-bottom, 0px));
        color: var(--app-color-text);
    }

    .ui-showcase-heading {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        max-width: 1100px;
        margin: 0 auto 20px;
        padding: 12px 4px;
    }

    .ui-showcase-heading h1 {
        margin: 2px 0 4px;
        color: var(--app-color-surface);
        font-size: var(--app-font-title);
    }

    .ui-showcase-heading p,
    .ui-showcase-note {
        margin: 0;
        color: rgba(255, 255, 255, 0.72);
        font-size: var(--app-font-small);
    }

    .ui-showcase-eyebrow {
        color: var(--app-color-secondary);
        font-size: var(--app-font-tiny);
        font-weight: 700;
        letter-spacing: 0.12em;
    }

    .ui-showcase-section {
        max-width: 1100px;
        margin: 0 auto 24px;
    }

    .ui-showcase-section > h2 {
        margin: 0 0 10px 4px;
        color: var(--app-color-surface);
        font-size: var(--app-font-emphasis);
        font-weight: 600;
    }

    .ui-palette-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        gap: 8px;
    }

    .ui-palette-swatch,
    .ui-showcase-card {
        overflow: hidden;
        border: 1px solid rgba(255, 255, 255, 0.14);
        border-radius: 12px;
        background: var(--app-color-surface) !important;
    }

    .ui-palette-color {
        height: 34px;
    }

    .ui-palette-copy {
        display: flex;
        justify-content: space-between;
        gap: 6px;
        padding: 7px 9px;
        font-size: var(--app-font-caption);
    }

    .ui-palette-copy span {
        color: var(--app-color-text-muted);
        font-family: monospace;
    }

    .ui-control-grid,
    .ui-form-grid,
    .ui-chip-row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 12px;
    }

    .ui-form-grid > * {
        flex: 1 1 220px;
    }

    .ui-slider-control {
        flex: 1 1 100%;
        padding: 8px 4px 0;
        color: var(--app-color-text-muted);
    }

    .ui-progress-row {
        display: flex;
        align-items: center;
        gap: 16px;
        margin-top: 18px;
    }

    .ui-dialog-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(175px, 1fr));
        gap: 8px;
    }

    .ui-dialog-launcher {
        justify-content: flex-start;
        min-height: 42px;
        padding-inline: 10px !important;
        text-transform: none;
    }

    .ui-showcase-preview-exit {
        position: fixed;
        z-index: 10000;
        right: 12px;
        bottom: calc(68px + env(safe-area-inset-bottom, 0px));
    }

    @media (max-width: 600px) {
        .ui-showcase-heading {
            align-items: flex-start;
        }

        .ui-showcase-heading p {
            max-width: 220px;
        }

        .ui-palette-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }
    }
</style>
